import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, X, Share, Smartphone } from "lucide-react";

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent || "");
}

function isMobile() {
  return /android|iphone|ipad|ipod/i.test(window.navigator.userAgent || "") || window.innerWidth < 768;
}

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    if (!isMobile() || isStandalone()) return;

    const dismissedAt = Number(localStorage.getItem("emedbay_pwa_dismissed_at") || 0);
    const cooldownMs = 1000 * 60 * 60 * 24 * 3;
    if (dismissedAt && Date.now() - dismissedAt < cooldownMs) return;

    setIos(isIOS());
    setVisible(true);

    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };

    const onInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
      localStorage.setItem("emedbay_pwa_installed", "1");
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem("emedbay_pwa_dismissed_at", String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice.catch(() => null);
    setDeferredPrompt(null);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="no-print fixed inset-x-3 bottom-3 z-[9999] md:hidden">
      <div className="rounded-2xl bg-slate-950 text-white shadow-2xl border border-white/10 p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center shrink-0">
            <Smartphone size={20} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="font-bold text-base">Install eMedbay</div>
            <div className="text-xs text-slate-300 mt-1 leading-relaxed">
              Add eMedbay to your Home Screen for faster doctor queue and reservation access.
            </div>

            {ios ? (
              <div className="mt-3 rounded-xl bg-white/10 p-3 text-xs text-slate-100 space-y-1">
                <div className="flex items-center gap-2">
                  <Share size={14} />
                  Tap <b>Share</b>
                </div>
                <div>Then choose <b>Add to Home Screen</b>.</div>
              </div>
            ) : null}

            <div className="mt-3 flex gap-2">
              {!ios && deferredPrompt ? (
                <Button size="sm" onClick={install} className="bg-blue-600 hover:bg-blue-700">
                  <Download size={14} className="mr-1" />
                  Install
                </Button>
              ) : null}

              <Button size="sm" variant="outline" onClick={dismiss} className="bg-white/5 border-white/20 text-white hover:bg-white/10">
                Not now
              </Button>
            </div>
          </div>

          <button onClick={dismiss} className="text-slate-400 hover:text-white">
            <X size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
